import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type { SyncHasMany } from '@ember-data/model';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type DetailedChangeset } from 'ember-changeset/types';
import { pluralize } from 'ember-inflector';
import PowerSelect from 'ember-power-select/components/power-select';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { and } from 'ember-truth-helpers';
import type EmployeeModel from 'garaje/models/employee';
import type GroupInvite from 'garaje/models/group-invite';
import type InviteModel from 'garaje/models/invite';
import type RecurringGroupInvite from 'garaje/models/recurring-group-invite';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import { isChangesetModel } from 'garaje/utils/is-model';

const MAX_ADDITIONAL_HOSTS = 9;

type FieldChangeset = DetailedChangeset<SignInFieldModel>;
type SupportedInvite = InviteModel | GroupInvite | RecurringGroupInvite;

interface InvitesSinglePreregHostsFieldSignature {
  Args: {
    additionalHosts: EmployeeModel[] | SyncHasMany<EmployeeModel>;
    allowClear: boolean;
    disabled?: boolean;
    errors?: string[] | string;
    field: FieldChangeset | SignInFieldModel;
    inviteChangeset?: DetailedChangeset<SupportedInvite>;
    changeset?: DetailedChangeset<SignInFieldModel>;
    primaryHost: EmployeeModel | null;
    required: boolean;
    searchEmployees: (term: string) => Promise<EmployeeModel[]>;
    updateAdditionalHosts: (hosts: EmployeeModel[]) => unknown;
    updatePrimaryHost: (field: FieldChangeset | SignInFieldModel, host: EmployeeModel) => unknown;
  };
  Element: HTMLDivElement;
}

export default class InvitesSinglePreregHostsField extends Component<InvitesSinglePreregHostsFieldSignature> {
  @tracked _showingAdditionalHostsSelect = false;

  maximumNumberOfAdditionalHosts = MAX_ADDITIONAL_HOSTS;

  get isMultiLocationInvite(): boolean {
    const { inviteChangeset } = this.args;
    if (!inviteChangeset || !isChangesetModel(inviteChangeset, 'invite')) return false;

    return <number>inviteChangeset.childInviteLocations?.length > 0;
  }

  get additionalHostsDisabledTooltip(): string | null {
    if (!this.isAdditionalHostsFieldDisabled) return null;

    const isRecurringInvite =
      !!this.args.inviteChangeset?.recurringRule && isChangesetModel(this.args.inviteChangeset, 'invite');
    if (isRecurringInvite && this.isMultiLocationInvite) {
      return 'Additional hosts are not available with recurring invites and multiple locations selected.';
    } else if (isRecurringInvite) {
      return 'Additional hosts are not available with recurring invites.';
    } else if (this.isMultiLocationInvite) {
      return 'Additional hosts are not available with multiple locations selected.';
    }

    return null;
  }

  get allowPrimaryHostToBeCleared(): boolean {
    // don't allow primary host to be cleared if @allowClear={{false}}
    if (!this.args.allowClear) return false;
    // only allow primary host to be cleared if no additional hosts are selected
    return this.args.additionalHosts.length === 0;
  }

  // Disable "additional hosts" field if any of these conditions are true:
  // * @disabled={{true}}
  // * no primary host is selected
  // * this component is being used with an entry and not an invite
  // * no additional hosts have been added and the invite is recurring
  // * no additional hosts have been added and the invite is multi-location
  get isAdditionalHostsFieldDisabled(): boolean {
    if (this.args.disabled) return true;
    if (!this.args.primaryHost) return true;
    if (this.isForEntry) return true;

    if (
      this.args.additionalHosts.length === 0 &&
      this.args.inviteChangeset?.recurringRule &&
      isChangesetModel(this.args.inviteChangeset, 'invite')
    )
      return true;
    if (this.args.additionalHosts.length === 0 && this.isMultiLocationInvite) return true;

    return false;
  }

  get hasAdditionalHosts(): boolean {
    return <number>this.args.additionalHosts.length > 0;
  }

  get hasErrors(): boolean {
    return !!this.args.errors && this.args.errors.length > 0;
  }

  get hasMaximumNumberOfAdditionalHosts(): boolean {
    return <number>this.args.additionalHosts.length >= MAX_ADDITIONAL_HOSTS;
  }

  get hostLabel(): string {
    if (this.hasAdditionalHosts || this.showingAdditionalHostsSelect) {
      return 'Primary host';
    }
    return 'Host';
  }

  get isForEntry(): boolean {
    return !this.args.inviteChangeset;
  }

  get showingAdditionalHostsSelect(): boolean {
    return this.isForEntry ? this.hasAdditionalHosts : this._showingAdditionalHostsSelect || this.hasAdditionalHosts;
  }

  get canAddAdditionalHosts(): boolean {
    return !this.showingAdditionalHostsSelect && !this.isForEntry;
  }

  @action
  handleAdditionalHostsSelectOpen(/* select: Select, e: Event */): boolean {
    // called when "additional hosts" select is opened; if there are already the maximum number
    // of allowable additional hosts, prevent opening so no more can be added.
    if (this.hasMaximumNumberOfAdditionalHosts) return false;
    return true;
  }

  @action
  async searchEmployees(searchTerm: string): Promise<EmployeeModel[]> {
    const employees = await this.args.searchEmployees(searchTerm);
    const existingHostIds = this.args.additionalHosts.map((host) => host.id);
    if (this.args.primaryHost) {
      existingHostIds.push(this.args.primaryHost.id);
    }
    // exclude employees who are already added to this invite, whether as primary host or as an additional host
    return employees.slice().filter((employee) => !existingHostIds.includes(employee.id));
  }

  @action
  showAdditionalHosts(): void {
    this._showingAdditionalHostsSelect = true;
  }

  @action
  updatePrimaryHost(host: EmployeeModel): void {
    this.args.updatePrimaryHost(this.args.field, host);
  }

  <template>
    <div data-test-flow-field="Host" data-test-custom-field="Host" ...attributes>
      <label
        data-test-select-label
        for={{@changeset.id}}
        class="{{if this.hasErrors 'error-description'}} {{if @disabled 'cursor-default'}}"
      >
        {{this.hostLabel}}
        {{#if @required}}
          <span class="requiredField"></span>
        {{/if}}
      </label>
      <div class="w-full flex gap-x-4 items-baseline">
        <div class="flex-1" data-test-primary-host-select>
          <PowerSelect
            @allowClear={{this.allowPrimaryHostToBeCleared}}
            @disabled={{@disabled}}
            @onChange={{this.updatePrimaryHost}}
            @placeholder="Choose a host"
            @search={{this.searchEmployees}}
            @searchEnabled={{true}}
            @selected={{@primaryHost}}
            @triggerClass={{if this.hasErrors "power-select-error"}}
            as |employee|
          >
            {{employee.name}}
          </PowerSelect>
        </div>
        {{#if this.canAddAdditionalHosts}}
          <div class="w-1/4">
            <button
              class="btn btn--link !text-gem-60"
              type="button"
              data-test-add-additional-hosts
              {{on "click" this.showAdditionalHosts}}
            >
              Add other hosts
            </button>
          </div>
        {{/if}}
      </div>
      {{#if this.hasErrors}}
        <div data-test-input-error class="bottom-error">This is a required field</div>
      {{/if}}

      {{#if this.showingAdditionalHostsSelect}}
        <label for="{{@changeset.id}}-additional-hosts">
          Additional hosts
          <span>
            <img
              width="16"
              height="16"
              src="/assets/images/gray-info.svg"
              class="ml-2"
              id="multiple-hosts-invite-info"
              alt="info"
            />
            <EmberTooltip @side="top">
              {{#if (and this.isAdditionalHostsFieldDisabled this.additionalHostsDisabledTooltip)}}
                {{this.additionalHostsDisabledTooltip}}
              {{else}}
                <p>Visitors will only see the primary host on their invite email.</p>
                <p>Additional hosts will receive a confirmation email once invite is sent.</p>
              {{/if}}
            </EmberTooltip>
          </span>
        </label>
        <div class="mb-[20px] w-full" data-test-additional-hosts-select>
          <PowerSelectMultiple
            @disabled={{this.isAdditionalHostsFieldDisabled}}
            @onChange={{@updateAdditionalHosts}}
            @onOpen={{this.handleAdditionalHostsSelectOpen}}
            @placeholder="Choose additional hosts"
            @search={{this.searchEmployees}}
            @searchEnabled={{true}}
            @searchField="name"
            @selected={{@additionalHosts}}
            as |employee|
          >
            {{employee.name}}
          </PowerSelectMultiple>
        </div>
        {{#if this.hasMaximumNumberOfAdditionalHosts}}
          <div class="bottom-error" data-test-max-additional-hosts-message>
            You've reached your maximum of
            {{pluralize this.maximumNumberOfAdditionalHosts "additional host"}}.
          </div>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
